import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Combobox } from '../../components/ui/Combobox';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { RowMenu } from '../../components/ui/RowMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatTile } from '../../components/ui/StatTile';
import { Toast } from '../../components/ui/Toast';
import { useOptions } from '../../hooks/useOptions';
import { formatIsraelTime } from '../../utils/formatters';
import { api } from '../../lib/api';

const DIRECTIONS = [
  { value: 'decrease', label: 'Decreases' },
  { value: 'increase', label: 'Increases' },
  { value: 'any', label: 'Changes (either way)' },
];
const TIMEFRAMES = [
  { value: 'day', label: 'Previous day' },
  { value: 'week', label: 'Previous 7 days' },
  { value: 'month', label: 'Previous month' },
];
const SCHEDULES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];
const WEEKDAYS = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
];
const WEEKDAY_LABEL = Object.fromEntries(WEEKDAYS.map((d) => [d.value, d.label]));
// The client is in Israel, so alert schedule times are shown/entered in Israel time.
// The timezone is stated once (on the field label / summary), not on every option.
const SCHEDULE_TZ_LABEL = 'Israel time';
const fmtHour = (h) => `${String(h).padStart(2, '0')}:00`;
// Whole-hour options for the schedule time (type to filter, or pick).
const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: fmtHour(h) }));

const DIR_WORD = { decrease: 'drops', increase: 'rises', any: 'changes' };
const DIR_SIGN = { decrease: '↓', increase: '↑', any: '↕' };
const TF_WORD = { day: 'previous day', week: 'previous 7 days', month: 'previous month' };
const TF_SHORT = { day: '1d', week: '7d', month: '30d' };

// Plain-English rule summary, e.g. "Cove MBX at AM MADA — usage drops ≥ 20% vs previous 7 days"
const condLabel = (r) =>
  `${r.product || 'Any product'} at ${r.reseller || 'any reseller'} — usage ${DIR_WORD[r.direction]} ≥ ${r.thresholdPct}% vs ${TF_WORD[r.timeframe]}`;

// Small rounded tag used to render a rule's scope/condition compactly.
const PILL_TONE = {
  slate: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
  brand: 'bg-brand/10 text-brand-dark dark:bg-brand/20 dark:text-brand-light',
  red: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
  green: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300',
};
function Pill({ children, tone = 'slate' }) {
  return <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ${PILL_TONE[tone]}`}>{children}</span>;
}

// Icon shown in the rule row + empty state.
const BellIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M10 2.5a4.5 4.5 0 00-4.5 4.5c0 3-1.5 4.5-1.5 4.5h12s-1.5-1.5-1.5-4.5A4.5 4.5 0 0010 2.5z" strokeLinejoin="round" />
    <path d="M8.5 14.5a1.5 1.5 0 003 0" strokeLinecap="round" />
  </svg>
);

const fmtWhen = formatIsraelTime;

// Small uppercase divider label that groups related fields in the rule form.
function SectionLabel({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{children}</p>
  );
}

function StatusPill({ enabled }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${enabled ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300' : 'bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-green-500' : 'bg-slate-400'}`} />
      {enabled ? 'active' : 'paused'}
    </span>
  );
}

const EMPTY_FORM = {
  name: '', reseller: '', product: '', direction: 'decrease',
  thresholdPct: '20', timeframe: 'week', schedule: 'daily', scheduleHour: '3', scheduleDay: 'Mon',
};

export default function Alerts() {
  const { resellers, products } = useOptions();
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Create/Edit modal — `editing` holds the rule being edited (null = creating).
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // `silent` refreshes data in place after an action, without swapping the table
  // for the spinner (the spinner is only for the initial page load).
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([api.get('/api/alerts'), api.get('/api/alerts/events')])
      .then(([r, e]) => { setRules(r); setEvents(e); })
      .catch((e) => setMsg({ type: 'error', text: e.message }))
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-dismiss the toast after a few seconds.
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(null), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const stats = useMemo(() => {
    const active = rules.filter((r) => r.enabled).length;
    return { total: rules.length, active, paused: rules.length - active, triggers: events.length };
  }, [rules, events]);

  const openCreate = () => {
    setMsg(null);
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (rule) => {
    setMsg(null);
    setEditing(rule);
    setForm({
      name: rule.name,
      reseller: rule.reseller || '',
      product: rule.product || '',
      direction: rule.direction,
      thresholdPct: String(rule.thresholdPct),
      timeframe: rule.timeframe,
      schedule: rule.schedule,
      scheduleHour: String(rule.scheduleHour),
      scheduleDay: rule.scheduleDay || 'Mon',
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name,
        reseller: form.reseller || null,
        product: form.product || null,
        direction: form.direction,
        thresholdPct: Number(form.thresholdPct),
        timeframe: form.timeframe,
        schedule: form.schedule,
        scheduleHour: Number(form.scheduleHour),
        scheduleDay: form.scheduleDay,
      };
      if (editing) await api.patch(`/api/alerts/${editing.id}`, body);
      else await api.post('/api/alerts', body);
      setMsg({ type: 'success', title: editing ? 'Rule updated' : 'Rule created', text: `“${form.name}” has been saved.` });
      setOpen(false);
      load(true);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      await api.patch(`/api/alerts/${rule.id}`, { enabled: !rule.enabled });
      load(true);
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  // Evaluate a rule right now (test) — opens a Freshdesk ticket if it matches.
  const doRunNow = async (rule) => {
    setMsg(null);
    try {
      const r = await api.post(`/api/alerts/${rule.id}/run`);
      const outcomes = {
        triggered: { title: 'Alert fired', text: r.ticketId ? `Freshdesk ticket #${r.ticketId} opened for “${rule.name}”.` : `“${rule.name}” matched, but no ticket was created (Freshdesk not configured).` },
      };
      const outcome = outcomes[r.action] || { title: 'No change', text: `“${rule.name}” is within its threshold — nothing to alert.` };
      setMsg({ type: 'success', ...outcome });
      load(true);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.del(`/api/alerts/${confirmDelete.id}`);
      setMsg({ type: 'success', title: 'Rule deleted', text: `“${confirmDelete.name}” has been removed.` });
      setConfirmDelete(null);
      load(true);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name', label: 'Rule',
      render: (_v, r) => {
        const dirTone = r.direction === 'increase' ? 'green' : r.direction === 'decrease' ? 'red' : 'slate';
        return (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand dark:bg-brand/20 dark:text-brand-light">
              <BellIcon />
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-navy-800 dark:text-slate-100" title={condLabel(r)}>{r.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Pill tone="brand">{r.product || 'All products'}</Pill>
                <Pill>{r.reseller || 'All resellers'}</Pill>
                <Pill tone={dirTone}>{DIR_SIGN[r.direction]} {r.thresholdPct}% · {TF_SHORT[r.timeframe]}</Pill>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'schedule', label: 'Checked',
      render: (_v, r) => (
        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <svg className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <circle cx="10" cy="10" r="7" />
            <path d="M10 6v4l2.5 1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{r.schedule === 'weekly' ? `Weekly · ${WEEKDAY_LABEL[r.scheduleDay] || 'Monday'}` : 'Daily'}</span> · {fmtHour(r.scheduleHour)} {SCHEDULE_TZ_LABEL}
        </span>
      ),
    },
    { key: 'enabled', label: 'Status', render: (v) => <StatusPill enabled={v} /> },
    {
      key: 'lastTriggeredAt', label: 'Last triggered',
      render: (v) => <span className="text-slate-500 dark:text-slate-400">{fmtWhen(v)}</span>,
    },
    {
      key: 'id', label: '', align: 'right',
      render: (_v, r) => (
        <div className="flex justify-end">
          <RowMenu
            items={[
              {
                label: 'Run now',
                icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M6 4.5l9 5.5-9 5.5v-11z" strokeLinejoin="round" /></svg>,
                onClick: () => doRunNow(r),
              },
              {
                label: 'Edit rule',
                icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M13.5 3.5l3 3L7 16H4v-3l9.5-9.5z" strokeLinejoin="round" /></svg>,
                onClick: () => openEdit(r),
              },
              {
                label: r.enabled ? 'Pause' : 'Resume',
                icon: r.enabled
                  ? <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M7 4.5v11M13 4.5v11" strokeLinecap="round" /></svg>
                  : <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M6.5 4.5l9 5.5-9 5.5v-11z" strokeLinejoin="round" /></svg>,
                onClick: () => toggleEnabled(r),
              },
              {
                label: 'Delete rule',
                danger: true,
                icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 6h12M8 6V4h4v2M6.5 6l.7 10h5.6l.7-10" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                onClick: () => setConfirmDelete(r),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Alert Triggers">
      <Toast msg={msg} onDismiss={() => setMsg(null)} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatTile label="Total rules" value={stats.total} icon={<BellIcon className="h-5 w-5" />} />
        <StatTile label="Active" value={stats.active} tone={stats.active ? 'green' : undefined}
          icon={(
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M2.5 10.5h3l2-5 3 9 2-4h4.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )} />
        <StatTile label="Paused" value={stats.paused} tone={stats.paused ? 'amber' : undefined}
          icon={(
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M7 4.5v11M13 4.5v11" strokeLinecap="round" />
            </svg>
          )} />
        <StatTile label="Triggers logged" value={stats.triggers}
          icon={(
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M11 2.5 4 11h4.5l-.5 6.5L15 9h-4.5l.5-6.5z" strokeLinejoin="round" />
            </svg>
          )} />
      </div>

      <Card
        title="Alert rules"
        subtitle="Open a Freshdesk ticket automatically when a reseller's product usage moves past your threshold."
        actions={(
          <Button onClick={openCreate} className="gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
            Add rule
          </Button>
        )}
      >
        {loading ? <Spinner /> : (
          rules.length
            ? <Table columns={columns} rows={rules} pageSize={10} />
            : <EmptyState message="No alert rules yet — create the first one." />
        )}
      </Card>

      <Card title="Recent triggers" subtitle="Alerts that have fired, and the usage change that set them off.">
        {events.length ? (
          <Table
            columns={[
              { key: 'ruleName', label: 'Rule' },
              { key: 'firedAt', label: 'When', render: (v) => fmtWhen(v) },
              {
                key: 'matches', label: 'Matched',
                render: (v) => {
                  const m = Array.isArray(v) ? v : [];
                  if (!m.length) return '—';
                  const first = m[0];
                  const head = `${first.product} @ ${first.reseller} ${first.change_pct > 0 ? '+' : ''}${first.change_pct}%`;
                  return m.length > 1 ? `${head} (+${m.length - 1} more)` : head;
                },
              },
              { key: 'freshdeskTicketId', label: 'Ticket', render: (v) => v || '—' },
            ]}
            rows={events}
            pageSize={10}
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand dark:bg-brand/20 dark:text-brand-light">
              <BellIcon className="h-6 w-6" />
            </span>
            <div>
              <div className="text-sm font-medium text-navy-800 dark:text-slate-100">No alerts triggered yet</div>
              <div className="mx-auto mt-1 max-w-sm text-xs text-slate-400 dark:text-slate-500">
                When a rule crosses its threshold, it’ll show up here with the usage change and the Freshdesk ticket it opened.
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Create / Edit rule */}
      <Modal open={open} onClose={() => setOpen(false)} size="xl" title={editing ? `Edit "${editing.name}"` : 'New alert rule'}>
        <form onSubmit={save} className="space-y-5">
          <Input label="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name this alert rule" required />

          <div className="space-y-3">
            <SectionLabel>Scope</SectionLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Combobox label="Reseller" placeholder="Select reseller…" value={form.reseller}
                onChange={(v) => setForm({ ...form, reseller: v })} options={resellers} searchPlaceholder="Search reseller…"
                emptyOption="All resellers" />
              <Combobox label="Product" placeholder="Select product…" value={form.product}
                onChange={(v) => setForm({ ...form, product: v })} options={products} searchPlaceholder="Search product…"
                emptyOption="All products" />
            </div>
          </div>

          <div className="space-y-3">
            <SectionLabel>Condition</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <Select label="When usage" options={DIRECTIONS} value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })} />
              <Input label="By at least (%)" type="number" min="1" max="1000" step="any" value={form.thresholdPct}
                onChange={(e) => setForm({ ...form, thresholdPct: e.target.value })} required />
            </div>
            <Select label="Compared to" options={TIMEFRAMES} value={form.timeframe}
              onChange={(e) => setForm({ ...form, timeframe: e.target.value })} />
          </div>

          <div className="space-y-3">
            <SectionLabel>Schedule</SectionLabel>
            <div className={`grid gap-4 ${form.schedule === 'weekly' ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <Select label="Check" options={SCHEDULES} value={form.schedule}
                onChange={(e) => setForm({ ...form, schedule: e.target.value })} />
              {form.schedule === 'weekly' && (
                <Select label="On" options={WEEKDAYS} value={form.scheduleDay}
                  onChange={(e) => setForm({ ...form, scheduleDay: e.target.value })} />
              )}
              <Combobox label={`At (${SCHEDULE_TZ_LABEL})`} placeholder="Select time…"
                searchPlaceholder="Type an hour, e.g. 23" value={form.scheduleHour}
                onChange={(v) => setForm({ ...form, scheduleHour: v })} options={HOURS} />
            </div>
          </div>

          {/* Live plain-English preview of the rule being built. */}
          <div className="flex items-start gap-2.5 rounded-xl bg-brand/5 dark:bg-brand/10 px-3.5 py-3 ring-1 ring-brand/15 dark:ring-brand/25">
            <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M10 2.5a4.5 4.5 0 00-4.5 4.5c0 3-1.5 4.5-1.5 4.5h12s-1.5-1.5-1.5-4.5A4.5 4.5 0 0010 2.5z" strokeLinejoin="round" />
              <path d="M8.5 14.5a1.5 1.5 0 003 0" strokeLinecap="round" />
            </svg>
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              When <b className="font-semibold text-navy-800 dark:text-slate-100">{form.product || 'any product'}</b> at{' '}
              <b className="font-semibold text-navy-800 dark:text-slate-100">{form.reseller || 'any reseller'}</b>{' '}
              {form.direction === 'any' ? 'changes' : `${form.direction}s`} by{' '}
              <b className="font-semibold text-navy-800 dark:text-slate-100">≥ {form.thresholdPct || '—'}%</b> vs the {TF_WORD[form.timeframe]}, an alert triggers — checked{' '}
              {form.schedule === 'weekly' ? `weekly on ${WEEKDAY_LABEL[form.scheduleDay]}` : 'daily'} at {fmtHour(form.scheduleHour)} {SCHEDULE_TZ_LABEL}.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim() || !form.thresholdPct}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create rule'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete rule">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Delete <strong className="text-navy-800 dark:text-slate-100">{confirmDelete?.name}</strong>?
          Its trigger history will be removed too.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button type="button" disabled={deleting} onClick={doDelete} className="!bg-red-600 !text-white !shadow-none hover:!bg-red-700">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </AppShell>
  );
}
